import {ReactElement, useContext, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {OriginalRevenueOperationalMetricValidKey, UnitTypeScheduleValue, VersionType} from '../../../../__generated__/generated_types';
import {BudgetingType} from '../../../../BudgetingType';
import {getBudgetYearForProperty, useProperties} from '../../../../contexts/properties/PropertiesContext';
import {useSettings} from '../../../../contexts/settings/SettingsContext';
import {WeightFn} from '../base';

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";

import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import {Button} from '@zendeskgarden/react-buttons';
import "native-injects";
import {ImMagicWand} from 'react-icons/im';
import RevenueWizard from '../../../../components/revenue-wizard/RevenueWizard';
import {VZHotTableConfig} from '../../../../components/vz-ui/vz-hot-table/vz-hot-table-config';
import {IVZHotTableRow} from '../../../../components/vz-ui/vz-hot-table/VZHotTable';
import VZSaveStatus from '../../../../components/vz-ui/vz-save-status/VZSaveStatus';
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {AuthContext} from "../../../../contexts/AuthContext";
import {hideRevenueWizard} from '../../../../global/feature_checkers';
import {getMonths} from '../../../../utils/months-helper';
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import {useUTSVOperationalMetrics} from '../hooks/useUTSVOperationalMetrics';
import {OperationalFinancialMetrics} from '../operational-metrics';
import OperationalVZHotTable from '../OperationalVZHotTable';
import {buildUTSVTableConfig, buildUTSVTableData} from '../utils/operational-metric-helpers';


export const weightFnMoveOutRate: WeightFn<"moveOutRate"> = (value) => {
    const val = value as UnitTypeScheduleValue;
    return val["leaseExpirationCount"] || 0;
};

export default function MoveOutRates(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as {type: BudgetingType; propertyId: string};
    const auth = useContext(AuthContext);
    const {currentProperty} = useProperties();
    const {year} = useSettings();
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);

    const currentVersionYear = type == BudgetingType.BUDGET ? year + 1 : year;
    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const isRevWizHidden = hideRevenueWizard(auth.user);

    const {
        fetchMetricData,
        metricData,
        metricDataLoading,
        forecastLocks,
        lastSaved,
        savingValues,
        upsertValues,
    } = useUTSVOperationalMetrics({
        property: currentProperty,
        year: currentVersionYear,
        versionType,
    });

    useEffect(
        () => {
            const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
            if (updateWorkflowTab != false) {
                setSelectedTab(updateWorkflowTab as string);
            }
            fetchMetricData();
        },
        [type]
    );

    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const [wizardOpen, setWizardOpen] = useState(false);

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        if (!metricData || !forecastLocks?.forecastLocks || !currentProperty) {
            return {
                tableData: [],
                tableConfig: new VZHotTableConfig(),
            };
        }

        const tableData = buildUTSVTableData(
            metricData,
            OriginalRevenueOperationalMetricValidKey.MoveOutRate,
            versionType,
            currentProperty,
        );

        const tableConfig = buildUTSVTableConfig(
            tableData,
            versionType == VersionType.Budget ? forecastLocks?.forecastLocks?.property.budgetLocked : forecastLocks?.forecastLocks?.property.reforecastLocked,
            versionType,
            currentProperty.reforecastStartMonthIndex,
            "percent",
            "avg",
        );

        return {tableData, tableConfig};
    }, [metricData, forecastLocks?.forecastLocks]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow} />
                            <WorkflowPageLabel label="Move Out Rates" />
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved} />
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow} />
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>
                <div className={workflowCSS.bodyContainer}>
                    {currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <OperationalVZHotTable
                                    data={tableData}
                                    colWidths={[80]}
                                    colHeaders={["Year", ...getMonths(currentProperty), "Average"]}
                                    fixedRowsTop={1}
                                    fixedRowsBottom={tableData.filter(x => x.rowType == "historical").length}
                                    tableConfig={tableConfig}
                                    isDataLoading={metricDataLoading}
                                    isSearchable
                                    isUndoRedoable
                                    summaryColumns={[
                                        {
                                            col: 13,
                                            summaryFunction: "avg",
                                        },
                                    ]}
                                    onChange={(changes) => upsertValues(tableData, changes, budgetYear, OriginalRevenueOperationalMetricValidKey.MoveOutRate)}
                                    customControls={
                                        currentProperty.isAcquisitionProperty ||
                                        !forecastLocks?.forecastLocks ||
                                        forecastLocks?.forecastLocks?.property.reforecastLocked ||
                                        forecastLocks?.forecastLocks?.property.budgetLocked ||
                                        isRevWizHidden ?
                                        <></>
                                        :
                                        <Button isPrimary onClick={() => setWizardOpen(true)}>
                                            <Button.StartIcon><ImMagicWand /></Button.StartIcon>
                                            Revenue Wizard
                                        </Button>
                                    }
                                />
                                {!currentProperty?.isAcquisitionProperty &&
                                    <OperationalFinancialMetrics type={type} budgetYear={budgetYear} />
                                }
                            </div>
                        </>
                    }
                </div>
            </div>

            {
                metricData &&
                currentProperty &&
                !currentProperty?.isAcquisitionProperty &&
                !forecastLocks?.forecastLocks?.property.reforecastLocked &&
                !forecastLocks?.forecastLocks?.property.budgetLocked &&
                !isRevWizHidden &&
                    <RevenueWizard
                        property={currentProperty}
                        budgetYear={budgetYear}
                        isOpen={wizardOpen}
                        metricData={metricData}
                        onClose={dataSaved => {
                            setWizardOpen(false);

                            if (dataSaved) {
                                fetchMetricData();
                            }
                        }}
                    />
            }
        </ThemeProvider>
    );
}
